import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, MapPin } from 'lucide-react';
import { RESIDENCES as DATA } from '../data/content';
import { RESIDENCE_DETAIL } from '../constants/testIds';
import Enquiry from '../components/aravi/Enquiry';

export default function ResidenceDetail() {
  const { slug } = useParams();
  const r = DATA.find((x) => x.slug === slug);

  if (!r) {
    return (
      <div className="bg-[#F5F1E8] min-h-screen pt-40 pb-24 px-6 md:px-16">
        <div
          data-testid={RESIDENCE_DETAIL.notFound}
          className="max-w-2xl mx-auto text-center"
        >
          <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
            404 — Off the Map
          </span>
          <h1 className="font-serif text-5xl md:text-6xl text-[#18342A] mt-6 leading-tight">
            That residence isn&apos;t one of ours.
          </h1>
          <p className="mt-6 text-[#6D6A61] font-light">
            Return to the portfolio and explore the estates currently open for visit.
          </p>
          <Link
            to="/residences"
            className="inline-flex items-center gap-3 mt-10 bg-[#18342A] text-[#F5F1E8] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#C9A458] hover:text-[#18342A] transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.6} />
            All Residences
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={RESIDENCE_DETAIL.root}>
      {/* Hero image */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden bg-[#18342A]">
        <img
          src={r.gallery[0]}
          alt={r.name}
          className="absolute inset-0 w-full h-full object-cover kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#18342A]/85 via-[#18342A]/30 to-[#18342A]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#18342A]/60 via-transparent to-transparent" />
        <div className="grain-overlay absolute inset-0 pointer-events-none" />

        <div className="relative z-10 h-full flex flex-col justify-end pb-20 lg:pb-28">
          <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto w-full">
            <Link
              to="/residences"
              className="inline-flex items-center gap-2 text-[#C9A458] text-[10px] tracking-[0.4em] uppercase mb-8 hover:text-[#F5F1E8] transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.6} />
              Back to Portfolio
            </Link>
            <div className="flex items-center gap-2 text-[#C9A458] text-[10px] tracking-[0.4em] uppercase mb-4">
              <MapPin size={12} strokeWidth={1.5} />
              {r.location}
              <span className="ml-4 px-3 py-1 border border-[#C9A458]/40">{r.status}</span>
            </div>
            <h1 className="font-serif text-[#F5F1E8] text-5xl sm:text-6xl md:text-7xl lg:text-[110px] leading-[0.95] tracking-tight font-light">
              {r.name}
            </h1>
            <p className="mt-6 text-[#F5F1E8]/80 text-base md:text-lg font-light max-w-2xl">
              {r.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Specs band */}
      <section className="bg-[#18342A] text-[#F5F1E8] border-y border-[#C9A458]/20">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto py-10 lg:py-12 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
          <Stat label="Configuration" value={r.beds} />
          <Stat label="Built (Initial)" value={r.priceNote ? r.priceNote.replace('Includes ', '').replace(' G+1 home', '') : 'G+1'} />
          <Stat label="Land" value={r.acres} />
          <Stat label="From" value={r.priceFrom} accent />
        </div>
      </section>

      {/* Description + side */}
      <section className="bg-[#F5F1E8] py-24 lg:py-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto grid grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-7"
          >
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
              About this Home
            </span>
            <h2 className="font-serif text-[#18342A] text-4xl md:text-5xl lg:text-6xl mt-6 leading-tight font-light">
              Yours, finished.<br/>Expandable, on your terms.
            </h2>
            <p className="mt-8 text-[#6D6A61] text-base md:text-lg leading-relaxed font-light max-w-2xl">
              {r.description}
            </p>

            {r.expandable && (
              <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 border border-[#C9A458]/40 text-[#A66B45] text-[11px] tracking-[0.28em] uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A458]" />
                {r.expandable}
              </div>
            )}

            <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-5 text-[#18342A]">
              {[
                'Within HMDA Limits · clear title',
                'Total hill view from every plot',
                'Miyawaki forest belt around the community',
                'Natural farm · farm store on site',
                'Solar, rainwater harvesting, STP',
                '25 acres · 110 villas · 40+ amenities',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm font-light">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C9A458] shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-5 lg:pl-6"
          >
            <div className="bg-[#18342A] text-[#F5F1E8] p-8 lg:p-10">
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#C9A458]">
                Schedule a Private Visit
              </span>
              <h3 className="font-serif text-3xl md:text-4xl mt-6 leading-tight font-light">
                Walk {r.name} with us.
              </h3>
              <p className="mt-6 text-[#F5F1E8]/70 text-sm font-light leading-relaxed">
                Site visits are arranged on the same double-road that connects
                ORR Exit to our gate — twelve to twenty minutes from the city.
              </p>
              <Link
                to="/contact"
                data-testid={RESIDENCE_DETAIL.enquireCta}
                className="mt-10 inline-flex items-center gap-3 bg-[#C9A458] text-[#18342A] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#F5F1E8] transition-colors"
              >
                Enquire Privately
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-[#F5F1E8] pb-24 lg:pb-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
          <div className="hairline w-full mb-10" />
          <div className="flex items-baseline justify-between mb-10">
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
              The Residence in Frames
            </span>
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#6D6A61]">
              {r.gallery.length} Studies
            </span>
          </div>
          <div className="grid grid-cols-12 gap-4 lg:gap-6">
            {r.gallery.map((src, i) => (
              <motion.div
                key={i}
                data-testid={RESIDENCE_DETAIL.gallery(i)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`overflow-hidden ${
                  i === 0
                    ? 'col-span-12 md:col-span-8 aspect-[16/10]'
                    : i === 1
                    ? 'col-span-12 md:col-span-4 aspect-[4/5]'
                    : 'col-span-12 md:col-span-6 aspect-[4/3]'
                }`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Enquiry />
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div>
    <div className={`font-serif text-3xl md:text-4xl leading-none ${accent ? 'text-[#C9A458]' : 'text-[#F5F1E8]'}`}>
      {value}
    </div>
    <div className="mt-2 text-[10px] tracking-[0.4em] uppercase text-[#9BA58D]">{label}</div>
  </div>
);
